import {
  type MaybeRefOrGetter,
  watchEffect,
  toValue,
  ref,
  computed,
} from "vue";
import { wagmiConfig } from "./wagmi-config";
import { watchContractEvent, getBlockNumber, watchAccount } from "@wagmi/core";
import { getPublicClient } from "@wagmi/core";

// 2️⃣ Define the ERC-20 ABI (Transfer event + balanceOf function):
const ERC20_ABI = [
  {
    constant: true,
    inputs: [{ name: "_owner", type: "address" }],
    name: "balanceOf",
    outputs: [{ name: "balance", type: "uint256" }],
    type: "function",
  },
  {
    anonymous: false,
    inputs: [
      { indexed: true, name: "from", type: "address" },
      { indexed: true, name: "to", type: "address" },
      { indexed: false, name: "value", type: "uint256" },
    ],
    name: "Transfer",
    type: "event",
  },
];

export interface TokenToWatchBalance {
  tokenSymbol: string;
  tokenAddress: string;
  walletAddress: string;
  chainId: string;
}

/**
 * Enhanced Web3 token balance watcher with WebSocket disconnection handling.
 *
 * Features:
 * - Watches Transfer events for ERC-20 tokens using WebSocket connections
 * - Tracks the last processed block for each token
 * - Detects WebSocket disconnections and reconnections
 * - Automatically fetches missed Transfer events during disconnection periods
 * - Triggers the listener for relevant events (transfers to/from watched wallet)
 *
 * @param options Configuration object
 * @param options.tokenList Reactive list of tokens to watch
 * @param options.listener Callback function triggered when relevant Transfer events occur
 * @returns State object with debugging information about connection status and last processed blocks
 */
export function useWebThreeTokenListBalance(options: {
  tokenList: MaybeRefOrGetter<TokenToWatchBalance[]>;
  listener: (token: TokenToWatchBalance) => void;
}) {
  // Track the last processed block for each token to handle reconnections
  const lastProcessedBlocks = ref<Map<string, bigint>>(new Map());
  const isReconnecting = ref<Map<string, boolean>>(new Map());
  const wasDisconnected = ref(false);

  /**
   * Process transfer logs and trigger the listener if relevant
   */
  const processTransferLogs = async (
    logs: any[],
    token: TokenToWatchBalance,
  ) => {
    for (const log of logs) {
      const args = extractArgs(log.args);

      if (!args) continue;

      // Update the last processed block for this token
      if (log.blockNumber) {
        const currentLastBlock =
          lastProcessedBlocks.value.get(token.tokenAddress) || 0n;
        if (log.blockNumber > currentLastBlock) {
          lastProcessedBlocks.value.set(token.tokenAddress, log.blockNumber);
        }
      }

      // If user sent tokens → subtract; if user received → add:
      if (
        args.from.toLowerCase() === token.walletAddress.toLowerCase() ||
        args.to.toLowerCase() === token.walletAddress.toLowerCase()
      ) {
        options.listener(token);
      }
    }
  };

  /**
   * Fetch missed events during disconnection period
   */
  const fetchMissedEvents = async (token: TokenToWatchBalance) => {
    try {
      const publicClient = getPublicClient(wagmiConfig);
      if (!publicClient) {
        console.warn(
          "[useWebThreeTokenListBalance] Public client not available for fetching missed events",
        );
        return;
      }

      const lastBlock = lastProcessedBlocks.value.get(token.tokenAddress);
      if (!lastBlock) {
        console.info(
          "[useWebThreeTokenListBalance] No last block recorded for token:",
          token.tokenAddress,
        );
        return;
      }

      const currentBlock = await getBlockNumber(wagmiConfig);
      const fromBlock = lastBlock + 1n;

      // Only fetch if there are blocks to check
      if (fromBlock <= currentBlock) {
        console.log(
          `[useWebThreeTokenListBalance] Fetching missed events for ${token.tokenSymbol} from block ${fromBlock} to ${currentBlock}`,
        );

        // Fetch Transfer events for this specific token contract
        const logs = await publicClient.getLogs({
          address: token.tokenAddress as `0x${string}`,
          event: {
            type: "event",
            name: "Transfer",
            inputs: [
              { indexed: true, name: "from", type: "address" },
              { indexed: true, name: "to", type: "address" },
              { indexed: false, name: "value", type: "uint256" },
            ],
          },
          fromBlock,
          toBlock: currentBlock,
        });

        if (logs.length > 0) {
          console.log(
            `[useWebThreeTokenListBalance] Found ${logs.length} missed Transfer events for ${token.tokenSymbol}`,
          );
          await processTransferLogs(logs, token);
        }
      }
    } catch (error) {
      console.error(
        `[useWebThreeTokenListBalance] Error fetching missed events for ${token.tokenSymbol}:`,
        error,
      );
    }
  };

  /**
   * Check for missed events across all tokens
   */
  const checkMissedEventsForAllTokens = async (
    tokenList: TokenToWatchBalance[],
  ) => {
    console.log(
      "[useWebThreeTokenListBalance] Checking for missed events across all tokens after reconnection",
    );

    const missedEventPromises = tokenList.map(async (token) => {
      try {
        await fetchMissedEvents(token);
      } catch (error) {
        console.error(
          `[useWebThreeTokenListBalance] Error checking missed events for ${token.tokenSymbol}:`,
          error,
        );
      }
    });

    await Promise.allSettled(missedEventPromises);
  };

  /**
   * Initialize block tracking for each token
   */
  const initializeBlockTracking = async (tokenList: TokenToWatchBalance[]) => {
    try {
      const currentBlock = await getBlockNumber(wagmiConfig);

      for (const token of tokenList) {
        // Only set if we don't already have a last processed block for this token
        if (!lastProcessedBlocks.value.has(token.tokenAddress)) {
          lastProcessedBlocks.value.set(token.tokenAddress, currentBlock);
          console.log(
            `[useWebThreeTokenListBalance] Initialized block tracking for ${token.tokenSymbol} at block ${currentBlock}`,
          );
        }
      }
    } catch (error) {
      console.error(
        "[useWebThreeTokenListBalance] Error initializing block tracking:",
        error,
      );
    }
  };

  // Watch for connection changes to detect reconnections
  watchEffect((onCleanup) => {
    const tokenList = toValue(options.tokenList);

    if (!tokenList?.length) return;

    const unsubscribe = watchAccount(wagmiConfig, {
      onChange: async (account, prevAccount) => {
        const isNowConnected = account.isConnected;
        const wasConnected = prevAccount?.isConnected ?? false;

        // Detect reconnection: was disconnected, now connected
        if (!wasConnected && isNowConnected) {
          console.log(
            "[useWebThreeTokenListBalance] Connection restored, checking for missed events",
          );
          wasDisconnected.value = false;

          // Mark all tokens as potentially having missed events
          for (const token of tokenList) {
            isReconnecting.value.set(token.tokenAddress, true);
          }

          // Check for missed events across all tokens
          await checkMissedEventsForAllTokens(tokenList);

          // Reset reconnecting flags
          for (const token of tokenList) {
            isReconnecting.value.set(token.tokenAddress, false);
          }
        } else if (wasConnected && !isNowConnected) {
          console.log(
            "[useWebThreeTokenListBalance] Connection lost, will check for missed events on reconnection",
          );
          wasDisconnected.value = true;
        }
      },
    });

    onCleanup(() => {
      unsubscribe();
    });
  });

  watchEffect(async (onCleanup) => {
    const tokenList = toValue(options.tokenList);

    if (!tokenList?.length) {
      console.info(
        "[useWebThreeTokenListBalance] Token list is not defined.",
        options,
      );
      return;
    }

    console.log(
      "[useWebThreeTokenListBalance] Watching token balances for:",
      tokenList,
    );

    // Initialize block tracking for new tokens
    await initializeBlockTracking(tokenList);

    const unWatchers = tokenList.map((token) =>
      watchContractEvent(wagmiConfig, {
        address: token.tokenAddress as `0x${string}`,
        abi: ERC20_ABI,
        eventName: "Transfer",

        onLogs: async (logs) => {
          // If we were reconnecting, this means connection is restored
          if (isReconnecting.value.get(token.tokenAddress)) {
            console.log(
              `[useWebThreeTokenListBalance] Connection restored for ${token.tokenSymbol}, fetching missed events...`,
            );
            await fetchMissedEvents(token);
            isReconnecting.value.set(token.tokenAddress, false);
          }

          await processTransferLogs(logs, token);
        },

        onError: (error) => {
          console.error(
            `[useWebThreeTokenListBalance] watchContractEvent error for ${token.tokenSymbol}:`,
            error,
          );

          // Mark as reconnecting to trigger missed event fetching on next successful connection
          isReconnecting.value.set(token.tokenAddress, true);
        },
      }),
    );

    onCleanup(() => {
      for (const unWatcher of unWatchers) {
        unWatcher();
      }
    });
  });

  // Return useful state for debugging and monitoring
  return {
    lastProcessedBlocks: computed(() => lastProcessedBlocks.value),
    isReconnecting: computed(() => isReconnecting.value),
    wasDisconnected: computed(() => wasDisconnected.value),
  };
}

function extractArgs(args: unknown) {
  if (
    typeof args === "object" &&
    args !== null &&
    "from" in args &&
    "to" in args &&
    "value" in args
  ) {
    return {
      from: (args as any).from as string,
      to: (args as any).to as string,
      value: (args as any).value as bigint,
    };
  }

  return null;
}
